import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/tarifs-soins")({
  head: () => ({
    meta: [
      { title: "Tarifs des soins de santé naturelle | Rénergescence" },
      { name: "description", content: "Tarifs 2026 du cabinet Rénergescence à Toulouse : naturopathie 70€, massages 70 à 110€, soins énergétiques 50 à 60€. Réservation en ligne." },
      { property: "og:title", content: "Tarifs des soins — Rénergescence" },
      { property: "og:description", content: "Grille tarifaire 2026 des soins du cabinet Rénergescence à Toulouse." },
      { property: "og:url", content: "/tarifs-soins" },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/tarifs-soins" }],
  }),
  component: Page,
});

type Row = { name: string; duration: string; price: string; cat: "Santé naturelle" | "Techniques manuelles" | "Soins énergétiques"; note?: string };

const rows: Row[] = [
  { cat: "Santé naturelle", name: "Séance de naturopathie", duration: "≈ 1h / 1h30", price: "70 €", note: "Bilan complet et programme personnalisé" },
  { cat: "Techniques manuelles", name: "Massage Rénergescence", duration: "1h", price: "75 €" },
  { cat: "Techniques manuelles", name: "Massage Rénergescence", duration: "1h30", price: "110 €" },
  { cat: "Techniques manuelles", name: "Massage sportif", duration: "1h", price: "85 €" },
  { cat: "Techniques manuelles", name: "Massage femme enceinte", duration: "1h", price: "70 €" },
  { cat: "Techniques manuelles", name: "Soin Essentiel", duration: "1h massage + soin énergétique", price: "90 €" },
  { cat: "Soins énergétiques", name: "Soin magnétisme", duration: "Présentiel ou distance", price: "50 €" },
  { cat: "Soins énergétiques", name: "Soin énergétique global", duration: "Présentiel ou distance", price: "60 €" },
];

const catColor: Record<Row["cat"], string> = {
  "Santé naturelle": "#E7EDE2",
  "Techniques manuelles": "#F4E1D2",
  "Soins énergétiques": "#DDE7EA",
};

function Page() {
  return (
    <>
      <section className="pt-20 pb-10 md:pt-28">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <p className="eyebrow">Tarifs 2026</p>
          <h1 className="mt-5 font-display text-4xl font-light italic md:text-6xl">Tarifs des soins</h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed md:text-lg" style={{ color: "var(--color-muted-foreground)" }}>
            Une grille tarifaire claire pour l'ensemble des prestations du cabinet Rénergescence à Toulouse et Fonsorbes.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="mx-auto max-w-[1100px] px-6">
          <h2 className="sr-only">Grille tarifaire</h2>
          {/* Mobile cards */}
          <div className="grid gap-4 md:hidden">
            {rows.map((r) => (
              <div key={r.name + r.duration} className="rounded-2xl border bg-white p-5" style={{ borderColor: "#e8e0d8" }}>
                <span className="inline-block rounded-full px-3 py-1 text-[11px] uppercase tracking-[0.18em]" style={{ backgroundColor: catColor[r.cat], color: "#2D2B28" }}>{r.cat}</span>
                <h3 className="mt-3 font-display text-lg" style={{ color: "#2D2B28" }}>{r.name}</h3>
                <p className="mt-1 text-xs" style={{ color: "var(--color-muted-foreground)" }}>{r.duration}</p>
                {r.note && <p className="mt-2 text-[13px] italic" style={{ color: "var(--color-muted-foreground)" }}>{r.note}</p>}
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-display text-2xl" style={{ color: "var(--color-sage-text)" }}>{r.price}</span>
                  <Link to="/contact" className="rounded-full px-4 py-2 text-xs font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>Réserver</Link>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-3xl border bg-white md:block" style={{ borderColor: "#e8e0d8" }}>
            <table className="w-full text-left">
              <thead style={{ backgroundColor: "#F8F5F0" }}>
                <tr className="text-xs uppercase tracking-[0.18em]" style={{ color: "#6a6258" }}>
                  <th className="px-6 py-4">Catégorie</th>
                  <th className="px-6 py-4">Prestation</th>
                  <th className="px-6 py-4">Durée</th>
                  <th className="px-6 py-4">Tarif</th>
                  <th className="px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.name + r.duration} className={i % 2 ? "" : ""} style={{ borderTop: "1px solid #f0ebe4" }}>
                    <td className="px-6 py-5 align-top">
                      <span className="inline-block rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.18em]" style={{ backgroundColor: catColor[r.cat], color: "#2D2B28" }}>{r.cat}</span>
                    </td>
                    <td className="px-6 py-5">
                      <div className="font-display text-lg" style={{ color: "#2D2B28" }}>{r.name}</div>
                      {r.note && <div className="mt-1 text-[13px] italic" style={{ color: "var(--color-muted-foreground)" }}>{r.note}</div>}
                    </td>
                    <td className="px-6 py-5 text-sm" style={{ color: "var(--color-text)" }}>{r.duration}</td>
                    <td className="px-6 py-5 font-display text-xl" style={{ color: "var(--color-sage-text)" }}>{r.price}</td>
                    <td className="px-6 py-5 text-right">
                      <Link to="/contact" className="inline-block rounded-full px-5 py-2 text-xs font-medium uppercase tracking-[0.14em] text-white transition-opacity hover:opacity-90" style={{ backgroundColor: "#7A8A76" }}>
                        Réserver ma séance
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mx-auto mt-12 max-w-2xl space-y-3 text-center text-sm" style={{ color: "var(--color-muted-foreground)" }}>
            <p>Modes de paiement acceptés : espèces et virement bancaire.</p>
            <p>Annulation : merci de prévenir plus de 48 h à l'avance, sans quoi la séance sera due.</p>
            <p>Bons cadeaux disponibles, valables 6 mois — <Link to="/prestations" hash="bons-cadeaux" className="underline">commander un bon</Link>.</p>
          </div>

          <div className="mt-10 text-center">
            <Link to="/contact" className="inline-block rounded-full px-8 py-4 text-sm font-medium text-white" style={{ backgroundColor: "#7A8A76" }}>
              Réserver ma séance
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}